import { useQueryClient } from '@tanstack/react-query';
import { useMutation } from 'query';
import { handleAxiosError } from 'ui';
import type { ApiClientResponseError, ResponseErrorDataObject } from 'common/services';
import { type CommunityInvitationChangeRequest, changeRequest } from 'features/change-requests';
// eslint-disable-next-line @typescript-eslint/no-restricted-imports -- TODO: fix according to result of #67489
import { getEventsFeatureQueryKey } from 'features/events/queries/keys';
// eslint-disable-next-line @typescript-eslint/no-restricted-imports -- TODO: fix according to result of #67489
import { getUsersListQueryKey } from 'features/users/queries/keys';
import { getCommunityMembersKey, getCommunityQueryKey, getCommunityUsersKey } from '../keys';

const defaultMutateOnError = (error: ApiClientResponseError<ResponseErrorDataObject>) => {
  const errorMessage = error.response?.status === 422 ? 'errorUserAlreadyInvited' : 'Failed to invite users.';
  handleAxiosError(error, errorMessage);
};

export const useInviteUsersMutation = () => {
  const queryClient = useQueryClient();

  return useMutation<unknown, { id: string; invitees: { emails: string[]; phones: string[]; userIds: string[] } }>({
    defaultMutateOnError,
    async mutationFn({ id, invitees }) {
      const changeRequests: CommunityInvitationChangeRequest[] = [];

      for (const email of invitees.emails) {
        changeRequests.push({
          change_request: {
            kind: 'community_invitation',
            meta: { invitation_email: email },
            subject_id: id,
            subject_type: 'Community',
          },
        });
      }

      for (const phone of invitees.phones) {
        changeRequests.push({
          change_request: {
            kind: 'community_invitation',
            meta: { invitation_phone: phone },
            subject_id: id,
            subject_type: 'Community',
          },
        });
      }

      for (const userId of invitees.userIds) {
        changeRequests.push({
          change_request: {
            kind: 'community_invitation',
            subject_id: id,
            subject_type: 'Community',
            subject_user_id: userId,
          },
        });
      }

      const response = await changeRequest({ body: changeRequests });
      return response.data;
    },
    async onSuccess(_, params: { id: string }) {
      await queryClient.invalidateQueries({ queryKey: getCommunityQueryKey({ id: params.id }) });
      await queryClient.invalidateQueries({ queryKey: getCommunityMembersKey({ communityId: params.id }) });
      await queryClient.invalidateQueries({ queryKey: getCommunityUsersKey({}) });
      await queryClient.invalidateQueries({ queryKey: getUsersListQueryKey({}) });
      await queryClient.invalidateQueries({ queryKey: getEventsFeatureQueryKey() });
    },
  });
};
